import { useFetch } from "@4uhub/lib4uhub";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { memo, useCallback, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import EditorComponent from "../../../components/UI/TextEditor/Editor";
import { IReport } from "../../../models/report";
import {
  isEmptyReportDescription,
  isHtmlDescription,
  plainTextToHtml,
} from "./reportDescriptionHtml";
import { REPORTS_TRANSLATION_PATH, updateReportDescription } from "./shared";

const EDIT_TRANSLATION_PATH = REPORTS_TRANSLATION_PATH + "editDescription.";

const EDITOR_MENU_BAR = {
  textFormatting: true,
  fontSize: true,
  markFormatting: true,
  selectColor: true,
  linkFormatting: true,
  textAlign: true,
};

type TEditorMode = "visual" | "html";

interface IReportEditDescriptionButtonProps {
  report: IReport;
  onSaved: () => void;
}

const toEditorHtml = (description?: string | null) => {
  if (!description) return "";

  return isHtmlDescription(description)
    ? description
    : plainTextToHtml(description);
};

const ReportEditDescriptionButtonComponent: React.FC<
  IReportEditDescriptionButtonProps
> = ({ report, onSaved }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<TEditorMode>("visual");
  const [html, setHtml] = useState("");

  const { sendRequest, loading } = useFetch(updateReportDescription);

  const openHandler = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setHtml(toEditorHtml(report.description));
      setMode("visual");
      setOpen(true);
    },
    [report.description],
  );

  const closeHandler = useCallback(() => {
    if (loading) return;
    setOpen(false);
  }, [loading]);

  const saveHandler = useCallback(async () => {
    const { success } = await sendRequest({
      id: report.id,
      description: isEmptyReportDescription(html) ? null : html,
    });

    if (!success) return;

    setOpen(false);
    onSaved();
  }, [html, onSaved, report.id, sendRequest]);

  return (
    <>
      <Tooltip title={t(EDIT_TRANSLATION_PATH + "title")}>
        <IconButton
          size="small"
          aria-label={t(EDIT_TRANSLATION_PATH + "title") as string}
          onClick={openHandler}
        >
          <EditRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {}
      <Dialog
        open={open}
        onClose={closeHandler}
        onClick={(event) => event.stopPropagation()}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t(EDIT_TRANSLATION_PATH + "title")} - {report.name}
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={mode}
            onChange={(_, value: TEditorMode) => setMode(value)}
            sx={{ mb: 2 }}
          >
            <Tab value="visual" label={t(EDIT_TRANSLATION_PATH + "visual")} />
            <Tab value="html" label={t(EDIT_TRANSLATION_PATH + "html")} />
          </Tabs>

          {mode === "visual" ? (
            <EditorComponent
              value={html}
              onChange={setHtml}
              maxHeight="50vh"
              menuBarProps={EDITOR_MENU_BAR}
            />
          ) : (
            <Box>
              <TextField
                value={html}
                onChange={(event) => setHtml(event.target.value)}
                placeholder={
                  t(EDIT_TRANSLATION_PATH + "htmlPlaceholder") as string
                }
                multiline
                minRows={12}
                maxRows={24}
                fullWidth
                InputProps={{
                  sx: { fontFamily: "monospace", fontSize: "0.85rem" },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeHandler} disabled={loading}>
            {t(EDIT_TRANSLATION_PATH + "cancel")}
          </Button>
          <Button variant="contained" onClick={saveHandler} disabled={loading}>
            {t(EDIT_TRANSLATION_PATH + "save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const ReportEditDescriptionButton = memo(
  ReportEditDescriptionButtonComponent,
);
